import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SchemaMarkup from '../../components/base/SchemaMarkup';

const aboutSchema = [
  {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About MADLIENA Landmark Ltd - Redsail Laser Malta",
    "description": "Learn about MADLIENA Landmark Ltd, the official Malta distributor of Redsail laser machines since 2005. 18+ years of experience serving customers worldwide.",
    "url": "https://redsaillaser-malta.com/about",
    "mainEntity": {
      "@type": "Organization",
      "name": "MADLIENA Landmark Ltd",
      "foundingDate": "2005",
      "description": "Professional manufacturer and distributor of CO2 laser engraving and cutting machines",
      "numberOfEmployees": {
        "@type": "QuantitativeValue",
        "value": "50+"
      },
      "areaServed": {
        "@type": "Place",
        "name": "Malta and Europe"
      }
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://redsaillaser-malta.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "About Us",
        "item": "https://redsaillaser-malta.com/about"
      }
    ]
  }
];

const values = [
  {
    icon: 'ri-lightbulb-line',
    title: 'Innovation',
    description: 'Continuously developing cutting-edge laser technology to meet evolving market needs.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Quality',
    description: 'Rigorous quality control ensures every machine meets the highest standards.',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Support',
    description: 'Dedicated customer service and technical support throughout your journey.',
  },
  {
    icon: 'ri-global-line',
    title: 'Global Reach',
    description: 'Serving customers worldwide with reliable products and local support.',
  },
];

const milestones = [
  { year: '2005', title: 'Company Founded', description: 'REDSAIL established in Jinan, China' },
  { year: '2010', title: 'International Expansion', description: 'Began exporting to over 50 countries' },
  { year: '2015', title: 'Innovation Award', description: 'Received industry recognition for technology' },
  { year: '2020', title: 'Global Network', description: 'Established distributor network worldwide' },
];

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-emerald-500 font-semibold text-sm tracking-wider uppercase">
                About Us
              </span>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mt-3 mb-6">
                Leading Laser Technology Since 2005
              </h1>
              <p className="text-xl text-gray-600 mb-6">
                REDSAIL is a professional manufacturer of CO2 laser engraving and cutting machines, 
                dedicated to providing high-quality laser solutions for businesses worldwide.
              </p>
              <p className="text-lg text-gray-600">
                With over 18 years of experience, we've helped thousands of customers achieve their 
                production goals with our reliable, innovative laser machines.
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://static.readdy.ai/image/a51c03b2382e35c3e6bd6650ef5c1522/1a0c13d6d72d0c0d219a90b49835c06d.png"
                alt="REDSAIL Team and Headquarters"
                className="w-full h-96 object-cover rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-emerald-500 font-semibold text-sm tracking-wider uppercase">
              Our Values
            </span>
            <h2 className="text-4xl font-bold text-gray-900 mt-3">
              What Drives Us Forward
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'ri-shield-check-line',
                title: 'Quality First',
                description: 'We only offer machines that meet the highest standards of precision and reliability.',
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Customer Support',
                description: 'Dedicated local support team ready to assist you with any technical or operational needs.',
              },
              {
                icon: 'ri-lightbulb-line',
                title: 'Innovation',
                description: 'Bringing the latest laser technology advancements to the Maltese market.',
              },
              {
                icon: 'ri-hand-heart-line',
                title: 'Partnership',
                description: 'Building long-term relationships with our clients through trust and excellence.',
              },
            ].map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 text-center hover:shadow-lg transition-shadow"
              >
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${value.icon} text-3xl text-emerald-500`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Journey</h2>
            <p className="text-lg text-gray-600">
              Key milestones in our growth and development over the years.
            </p>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-emerald-200"></div>
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-8 ${
                    index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'
                  }`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'text-right' : 'text-left'}`}>
                    <div className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all">
                      <div className="text-3xl font-bold text-emerald-500 mb-2">
                        {milestone.year}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {milestone.title}
                      </h3>
                      <p className="text-gray-600">{milestone.description}</p>
                    </div>
                  </div>
                  <div className="w-4 h-4 bg-emerald-500 rounded-full border-4 border-white shadow-lg z-10"></div>
                  <div className="flex-1"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-emerald-500">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold text-white mb-2">18+</div>
              <div className="text-white/90">Years Experience</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-white mb-2">50+</div>
              <div className="text-white/90">Countries Served</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-white mb-2">10,000+</div>
              <div className="text-white/90">Machines Sold</div>
            </div>
            <div>
              <div className="text-5xl font-bold text-white mb-2">24/7</div>
              <div className="text-white/90">Customer Support</div>
            </div>
          </div>
        </div>
      </section>

      {/* Malta Distributor Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Official Malta Distributor
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            MADLIENA Landmark Ltd is the authorized distributor of REDSAIL laser machines in Malta, 
            providing local sales, support, and service to customers across the region.
          </p>
          <div className="bg-gray-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">MADLIENA Landmark Ltd</h3>
            <div className="space-y-2 text-gray-600">
              <p>47, Triq il Gladjoli, Madliena, Malta SWQ2023</p>
              <p>VAT No: MT 29045621</p>
              <p className="font-semibold text-gray-900 mt-4">Gordon Agius</p>
              <p>Phone: +356 9944 6552</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Ready to Work With Us?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Discover how our laser machines can transform your business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/products"
              className="px-8 py-4 bg-emerald-500 text-white font-semibold rounded-full hover:bg-emerald-600 transition-all whitespace-nowrap"
            >
              View Products
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 bg-gray-200 text-gray-900 font-semibold rounded-full hover:bg-gray-300 transition-all whitespace-nowrap"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
